/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import CKEditorCloudServicesError from '../ckeditorcloudserviceserror';
import CKEditorCloudServicesServerError from '../ckeditorcloudservicesservererror';
import { IMessage } from '../message';
import WebSocketGateway from './websocketgateway';

interface IWebSocketRequest<TResponse extends IMessage> {
	response( response: TResponse ): void;
	error( error: CKEditorCloudServicesError | CKEditorCloudServicesServerError ): void;
}

class WebSocketRequest<TResponse extends IMessage> implements IWebSocketRequest<TResponse> {
	private readonly _deferredPromise: IDeferredPromise<TResponse> = /* #__PURE__ -- @preserve */ _deferPromise();

	public readonly timeout: NodeJS.Timeout;

	public constructor(
		private readonly _context: WebSocketGateway,
		timeoutMs: number
	) {
		this.timeout = setTimeout(
			() => this.error( new CKEditorCloudServicesError( 'Request timeout.', this._context ) ),
			timeoutMs
		);
	}

	public get promise(): Promise<TResponse> {
		return this._deferredPromise.promise;
	}

	public response( response: TResponse ): void {
		this._deferredPromise.resolve( response );
	}

	public error( error: CKEditorCloudServicesError ): void {
		this._deferredPromise.reject( error );
	}
}

const DEFAULT_REQUEST_TIME: number = 45 * 1000;

export default class WebSocketGatewayRequestsManager {
	private readonly _requests: Set<WebSocketRequest<IMessage>> = new Set();

	public constructor(
		private readonly _context: WebSocketGateway
	) {}

	public async send<TResponse extends IMessage>(
		sendFunction: ( req: IWebSocketRequest<TResponse> ) => void,
		timeoutMs: number = DEFAULT_REQUEST_TIME
	): Promise<TResponse> {
		const req: WebSocketRequest<TResponse> = this._createRequest( timeoutMs );

		try {
			sendFunction( req );

			return await req.promise;
		} finally {
			this._finishRequest( req );
		}
	}

	public errorAll( error: CKEditorCloudServicesError ): void {
		for ( const req of this._requests ) {
			req.error( error );
		}
	}

	public waitForAllRequests( time: number = DEFAULT_REQUEST_TIME ): Promise<void> {
		return this._waitForRequests( time );
	}

	private _createRequest<TResponse extends IMessage>( timeoutMs: number ): WebSocketRequest<TResponse> {
		const req: WebSocketRequest<TResponse> = new WebSocketRequest( this._context, timeoutMs );

		this._requests.add( req );

		return req;
	}

	private _finishRequest( req: WebSocketRequest<IMessage> ): void {
		this._requests.delete( req );
		clearTimeout( req.timeout );
	}

	private async _waitForRequests( time: number = 5000, attempt: number = 0 ): Promise<void> {
		const waitAttemptTime: number = 500;

		if ( !this._requests.size || attempt >= time / waitAttemptTime ) {
			return Promise.resolve();
		}

		await new Promise( resolve => {
			setTimeout( resolve, waitAttemptTime );
		} );

		return this._waitForRequests( time, attempt++ );
	}
}

interface IDeferredPromise<T> {
	resolve: ( value?: ( PromiseLike<T> | T ) ) => void;
	reject: ( reason?: unknown ) => void;
	promise: Promise<T>;
}

function _deferPromise<T>(): IDeferredPromise<T> {
	let resolve: ( value?: T | PromiseLike<T> ) => void;
	let reject: ( reason?: unknown ) => void;

	// eslint-disable-next-line promise/param-names
	const promise: Promise<T> = new Promise( ( promiseResolve, promiseReject ) => {
		resolve = promiseResolve;
		reject = promiseReject;
	} );

	return { resolve: resolve!, reject: reject!, promise };
}
